// « Taps Sextant » — every card tap of a period, with its state (PMS-LEDGER-TAP-VIEW-BRIEF, T2).
//
// The period is the tap date. The open-day reading is shown with the time it was read, because
// the rollover is manual: a stale reading must be visible, never read as a clock.
// The audit switch « Afficher les rapprochés » is offered to `rec.taps.audit` only (the operator's
// role holds it; the desk does not). The database refuses the audit view without it as well.

import { Fragment, useEffect, useState } from 'react';
import type { CardTap, CardTapsRange, IsoDate } from '@lemontaigu/pms-core';
import { findMatchCandidates, type MatchCandidate } from './cardTapSearch.js';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsScope } from './PmsProvider.js';
import { fmtAmount } from './logic.js';
import {
  TAP_STATE_FR,
  addDays,
  barFromResto,
  centsToEuros,
  defaultRange,
  defaultSplit,
  frDay,
  isDeclarable,
  isStaleReading,
  parseEurosToCents,
  parisTime,
  rangeError,
  readingLabel,
  restoFromBar,
  visibleTaps,
} from './cardTapsView.js';

/** The search for one tap: the combinations of unmatched ledger card lines that match it. */
interface SearchState {
  tapUuid: string;
  tapCents: number;
  tapDay: IsoDate;
  loading: boolean;
  error: string | null;
  candidates: MatchCandidate[];
}

/** The declaration form of one tap: the two amounts as the operator types them (« 5,00 »). */
interface DeclareForm {
  tapUuid: string;
  totalCents: number;
  bar: string;
  resto: string;
}

interface TapsState {
  range: CardTapsRange | null;
  error: string | null;
  loading: boolean;
}

export function CardTapsSection({ today }: { today: IsoDate }) {
  const { cardTaps } = usePms();
  const { current } = usePmsScope();
  const allowed = useCapability(CAPABILITIES.FIN_FINDINGS_READ);
  const canAudit = useCapability(CAPABILITIES.REC_TAPS_AUDIT);

  const initial = defaultRange(today);
  const [from, setFrom] = useState<IsoDate>(initial.from);
  const [to, setTo] = useState<IsoDate>(initial.to);
  const [vegaOn, setVegaOn] = useState(false);
  const [lsOn, setLsOn] = useState(false);
  const [state, setState] = useState<TapsState>({ range: null, error: null, loading: false });
  const [reloadKey, setReloadKey] = useState(0);
  const [form, setForm] = useState<DeclareForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [search, setSearch] = useState<SearchState | null>(null);

  const rErr = rangeError(from, to);
  const show = { vega: canAudit && vegaOn, ls: canAudit && lsOn };
  // The server returns matched taps only when asked (audit); one switch on is enough to ask.
  const audit = show.vega || show.ls;

  useEffect(() => {
    if (!current || !allowed || rErr) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    cardTaps.range(current, from, to, audit).then((res) => {
      if (cancelled) return;
      setState(
        res.ok
          ? { range: res.value, error: null, loading: false }
          : { range: null, error: res.error.message ?? 'lecture impossible', loading: false },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [cardTaps, current, allowed, from, to, audit, rErr, reloadKey]);

  /** Typing in one field completes the other, so the two always add up to the tap. */
  const typeResto = (text: string) => {
    if (!form) return;
    const cents = parseEurosToCents(text);
    setForm({ ...form, resto: text, bar: cents === null ? form.bar : centsToEuros(barFromResto(form.totalCents, cents)) });
  };
  const typeBar = (text: string) => {
    if (!form) return;
    const cents = parseEurosToCents(text);
    setForm({ ...form, bar: text, resto: cents === null ? form.resto : centsToEuros(restoFromBar(form.totalCents, cents)) });
  };

  const openForm = (tapUuid: string, totalCents: number) => {
    const split = defaultSplit(totalCents);
    setFormError(null);
    setForm({ tapUuid, totalCents, bar: centsToEuros(split.barCents), resto: centsToEuros(split.restoCents) });
  };

  const confirm = async () => {
    if (!form || !current) return;
    const bar = parseEurosToCents(form.bar);
    const resto = parseEurosToCents(form.resto);
    if (bar === null || resto === null || bar + resto !== form.totalCents) {
      setFormError('Les deux montants doivent être des euros et additionner le paiement.');
      return;
    }
    setSaving(true);
    setFormError(null);
    const res = await cardTaps.declarePassage(current, form.tapUuid, bar, resto, '');
    setSaving(false);
    if (res.ok) {
      setNotice(`Vente déclarée : ${res.value.caseId} (passage, ${centsToEuros(bar)} bar · ${centsToEuros(resto)} restaurant)`);
      setForm(null);
      setReloadKey((k) => k + 1);
    } else {
      setFormError(res.error.message ?? 'Déclaration refusée');
    }
  };

  /** Looks for the unmatched card lines around the tap's day that add up to the tap. Proposes only. */
  const openSearch = async (t: CardTap) => {
    if (!current) return;
    setSearch({ tapUuid: t.tapUuid, tapCents: t.amountCents, tapDay: t.tapDay, loading: true, error: null, candidates: [] });
    const lines = await cardTaps.unmatchedCardLines(current, addDays(t.tapDay, -1), addDays(t.tapDay, 1));
    if (!lines.ok) {
      setSearch((s) => (s && s.tapUuid === t.tapUuid ? { ...s, loading: false, error: lines.error.message ?? 'lecture impossible' } : s));
      return;
    }
    const candidates = findMatchCandidates(t.amountCents, t.tapDay, lines.value);
    setSearch((s) => (s && s.tapUuid === t.tapUuid ? { ...s, loading: false, candidates } : s));
  };

  const declareCandidate = async (tapUuid: string, cand: MatchCandidate) => {
    if (!current) return;
    setSaving(true);
    const res = await cardTaps.declareMatch(current, tapUuid, cand.sourceIds, '');
    setSaving(false);
    if (res.ok) {
      setNotice(`Rapprochement déclaré : ${cand.sourceIds.join(' + ')} (écart ${centsToEuros(res.value.diffCents)} €)`);
      setSearch(null);
      setReloadKey((k) => k + 1);
    } else {
      setSearch((s) => (s && s.tapUuid === tapUuid ? { ...s, error: res.error.message ?? 'Rapprochement refusé' } : s));
    }
  };

  const { range, error, loading } = state;
  const rows = range ? visibleTaps(range.taps, show) : [];
  const stale = range ? isStaleReading(range.workingDayReadAt, Date.now()) : false;

  return (
    <section className="pms-print" aria-label="Taps Sextant">
      <div className="pms-head">
        <div>
          <h2>Taps Sextant — {frDay(from)} au {frDay(to)}</h2>
          <p className="pms-hint">
            Chaque paiement carte du terminal, rattaché à Vega ou non. Une tap sans paiement Vega n’est pas une erreur
            avant la clôture : la saisie peut arriver après.
          </p>
        </div>
      </div>

      <div className="pms-row">
        <label>
          Du{' '}
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value as IsoDate)} />
        </label>{' '}
        <label>
          au{' '}
          <input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value as IsoDate)} />
        </label>
        {canAudit && (
          <>
            <label className="pms-switch">
              {' '}
              <input type="checkbox" checked={vegaOn} onChange={(e) => setVegaOn(e.target.checked)} />{' '}
              Afficher les rapprochés Vega (audit)
            </label>{' '}
            <label className="pms-switch">
              <input type="checkbox" checked={lsOn} onChange={(e) => setLsOn(e.target.checked)} />{' '}
              Afficher les rapprochés Lightspeed (audit)
            </label>
          </>
        )}
      </div>

      {rErr && <p className="pms-warn">{rErr}</p>}
      {!allowed && <p className="pms-warn">Ces contrôles ne sont pas disponibles pour ce compte.</p>}

      {range && (
        <p className="pms-hint">
          Jour de travail Vega : <strong>{frDay(range.workingDay)}</strong> ({readingLabel(range.workingDayReadAt, today)}
          ) · jour clos : {frDay(range.closeDay)}
        </p>
      )}
      {notice && <p className="pms-hint">{notice}</p>}
      {range && stale && (
        <p className="pms-warn">
          Lecture du jour ancienne : le relevé horaire ne tourne peut-être plus. Le jour Vega peut avoir changé depuis.
        </p>
      )}

      {error && <p className="pms-warn">⚠ {error} — ne pas conclure à l’absence d’écart.</p>}
      {loading && !range && <p className="pms-hint">Lecture…</p>}

      {range && !error && rows.length === 0 && <p className="pms-hint">Aucune tap à traiter sur la période.</p>}

      {rows.length > 0 && (
        <table className="pms-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Heure</th>
              <th className="pms-num">Montant</th>
              <th>Contrat</th>
              <th>Carte</th>
              <th>État</th>
              <th>Détail</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <Fragment key={t.tapUuid}>
                <tr className={t.state === 'rattache' || t.state === 'hors_vega' ? 'pms-muted' : undefined}>
                  <td className="pms-nowrap">{frDay(t.tapDay)}</td>
                  <td className="pms-nowrap">{parisTime(t.tappedAt)}</td>
                  <td className="pms-num">{fmtAmount(t.amountCents)}</td>
                  <td>{t.contract ?? '—'}</td>
                  <td className="pms-nowrap">{t.cardMasked ?? '—'}</td>
                  <td>{TAP_STATE_FR[t.state]}</td>
                  <td>{t.detail}</td>
                  <td>
                    {canAudit && isDeclarable(t) && (
                      <>
                        <button type="button" onClick={() => openForm(t.tapUuid, t.amountCents)} disabled={saving}>
                          Déclarer une vente
                        </button>{' '}
                        <button type="button" onClick={() => openSearch(t)} disabled={saving}>
                          Chercher un rapprochement
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {search?.tapUuid === t.tapUuid && (
                  <tr>
                    <td colSpan={8}>
                      <strong>Rapprochements possibles — {centsToEuros(t.amountCents)} € ({frDay(t.tapDay)} ± 1 jour)</strong>
                      {search.loading && <p className="pms-hint">Recherche…</p>}
                      {search.error && <p className="pms-warn">{search.error}</p>}
                      {!search.loading && !search.error && search.candidates.length === 0 && (
                        <p className="pms-hint">Aucune combinaison de lignes de caisse non rapprochées ne correspond.</p>
                      )}
                      {search.candidates.length > 0 && (
                        <table className="pms-table">
                          <thead>
                            <tr>
                              <th>Lignes de caisse</th>
                              <th className="pms-num">Total</th>
                              <th className="pms-num">Écart</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {search.candidates.map((c, i) => (
                              <tr key={i}>
                                <td>
                                  {c.sourceIds.join(' + ')}
                                  <span className="pms-sub"> ({c.days.map((d) => frDay(d)).join(', ')})</span>
                                </td>
                                <td className="pms-num">{centsToEuros(c.ledgerCents)} €</td>
                                <td className="pms-num">{c.diffCents === 0 ? 'exact' : `${centsToEuros(c.diffCents)} €`}</td>
                                <td>
                                  <button type="button" onClick={() => declareCandidate(t.tapUuid, c)} disabled={saving}>
                                    Déclarer ce rapprochement
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
                {form?.tapUuid === t.tapUuid && (
                  <tr>
                    <td colSpan={8}>
                      <div className="pms-row">
                        <strong>Vente comptoir (passage) — {centsToEuros(form.totalCents)} € TTC</strong>{' '}
                        <label>
                          Bar 20 %{' '}
                          <input
                            inputMode="decimal"
                            value={form.bar}
                            onChange={(e) => typeBar(e.target.value)}
                            aria-label="Montant bar TTC"
                          />{' '}
                          €
                        </label>{' '}
                        <label>
                          Restaurant 10 %{' '}
                          <input
                            inputMode="decimal"
                            value={form.resto}
                            onChange={(e) => typeResto(e.target.value)}
                            aria-label="Montant restaurant TTC"
                          />{' '}
                          €
                        </label>{' '}
                        <button type="button" onClick={confirm} disabled={saving}>
                          Confirmer la déclaration
                        </button>{' '}
                        <button type="button" onClick={() => setForm(null)} disabled={saving}>
                          Annuler
                        </button>
                      </div>
                      {formError && <p className="pms-warn">{formError}</p>}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
